import { Label } from "@App/Components/atoms/common/Label";
import { ContractNumberHelpIcon } from "@App/Components/atoms/whitelabel/hints/ContractNumberHelpIcon";
import { SubmitButton } from "@App/Components/atoms/whitelabel/home/SubmitButton";
import { StepWrapper } from "@App/Components/atoms/whitelabel/home/stepper/StepWrapper";
import { Input } from "@App/Components/common/Input";
import { NumberPlateInput } from "@App/Components/NumberPlateInput";
import { useVehicleReturnContract } from "@App/hooks/useVehicleReturnContract";
import type { State } from "@App/state";
import type { Organization } from "@App/types";
import {
  OrganizationIsMeinAuto,
  OrganizationIsMobilityConcept,
} from "@App/utils/conditionals/organization";
import { getConfig } from "@App/utils/getConfig";
import type { FunctionComponent } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

type VehicleReturnContractStepProps = {
  contractNumber: string | null;
  numberPlate: string | null;
  setContractNumber: (value: string) => void;
  setNumberPlate: (value: string) => void;
  onSubmit: () => void;
};

function renderContractDetailsHint(organization: Organization) {
  if (OrganizationIsMobilityConcept(organization))
    return (
      <div className={"rounded p-3 border mb-4"}>
        <Trans
          components={{
            a: <a href={"/partner/meinauto?service=94"} target={"_blank"} rel="noopener" />,
            bold: <span className={"font-bold"} />,
            br: <br />,
          }}
          i18nKey="contractDetails.hint"
        />
      </div>
    );
  return <></>;
}

export const VehicleReturnContractStep: FunctionComponent<VehicleReturnContractStepProps> = ({
  contractNumber,
  numberPlate,
  setContractNumber,
  setNumberPlate,
  onSubmit,
}: VehicleReturnContractStepProps) => {
  const { t } = useTranslation();

  const organization = useSelector((state: State) => state.orders.organization);
  const { id: organizationId } = organization;

  const config = getConfig(organization);
  const contractNumberRegExp = config.checkout.contractNumber.regex;
  const canBook = config.contractDetails?.canBook;

  const { loading, error, isDataComplete, onClick } = useVehicleReturnContract({
    organizationId,
    contractNumber,
    numberPlate,
    onSubmit,
    contractNumberRegExp,
    canBook,
  });

  return (
    <StepWrapper>
      {renderContractDetailsHint(organization)}
      <div className="flex items-center gap-2">
        <Label
          htmlFor="contract-number"
          text={t("molecules.whitelabel.home.stepper.contractDataStep.contractNumber.label")}
        />
        {OrganizationIsMeinAuto(organization) && <ContractNumberHelpIcon />}
      </div>
      <Input
        id="contract-number"
        value={contractNumber ?? ""}
        onChange={setContractNumber}
        disabled={loading}
        type="text"
        placeholder={t(
          "molecules.whitelabel.home.stepper.contractDataStep.contractNumber.placeholder",
        )}
        className="mb-6 w-full"
      />
      <Label
        htmlFor="contract-number"
        text={t("molecules.whitelabel.home.stepper.contractDataStep.numberPlate.label")}
      />
      <div className="w-full md:w-80">
        <NumberPlateInput disabled={loading} value={numberPlate} onChange={setNumberPlate} />
      </div>
      {error && error === "general" && (
        <p className={"text-error-500"}>
          <Trans
            components={{
              a: <a className={"text-error-500"} href={"mailto:werkstattmeister@repareo.de"} />,
            }}
            i18nKey="contractDetails.error.general"
          />
        </p>
      )}
      {error && error === "wrong_format" && (
        <p className={"text-error-500"}>
          <Trans
            components={{
              a: (
                <a
                  className={"text-error-500"}
                  href={"/partner/meinauto?service=94"}
                  target={"_blank"}
                  rel={"noopener"}
                />
              ),
            }}
            i18nKey="contractDetails.error.wrong_format"
          />
        </p>
      )}
      {error && error === "not_found" && (
        <p className={"text-error-500"}>
          <Trans
            components={{
              a: (
                <a
                  className={"text-error-500"}
                  href={`mailto:${t("contractDetails.error.not_found_email")}`}
                />
              ),
            }}
            i18nKey="contractDetails.error.not_found"
          />
        </p>
      )}
      {error && error === "does_not_match" && (
        <p className={"text-error-500"}>
          <Trans
            components={{
              a: (
                <a
                  className={"text-error-500"}
                  href={`mailto:${t("contractDetails.error.not_found_email")}`}
                />
              ),
            }}
            i18nKey="contractDetails.error.does_not_match"
          />
        </p>
      )}
      {error && error === "wrong_contract_type" && (
        <p className={"text-error-500"}>
          <Trans
            components={{
              a: (
                <a
                  className={"text-error-500"}
                  href={"/partner/mobility-concept?service=94"}
                  target={"_blank"}
                  rel={"noopener"}
                />
              ),
            }}
            i18nKey="contractDetails.error.wrong_contract_type"
          />
        </p>
      )}
      {error && error === "cannot_book" && (
        <p className={"text-error-500"}>{t("contractDetails.error.cannot_book")}</p>
      )}
      <SubmitButton
        disabled={!isDataComplete || loading}
        text={t("molecules.whitelabel.home.stepper.contractDataStep.submit.label")}
        onClick={onClick}
      />
    </StepWrapper>
  );
};
